import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CalendarClock, Compass, RefreshCw, TrendingUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Guide, Loader, Pill } from "@/components/Bits";

const paceTone = { ahead: "green", on_track: "blue", behind: "amber" };
const paceLabel = { ahead: "Ahead of plan", on_track: "On track", behind: "Running behind" };

export default function Dashboard() {
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/plan/today");
    setToday(data);
    return data;
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const pendingMaterial = today?.items?.some((i) => !i.material_ready && i.status !== "completed");

  useEffect(() => {
    if (!pendingMaterial) return;
    const id = setInterval(() => load(), 8000);
    return () => clearInterval(id);
  }, [pendingMaterial, load]);

  const replan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plan/refresh");
      setToday(data);
      toast.success("Planner re-shaped your day");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!today) return <Shell><Loader label="Planner agent is shaping your day…" /></Shell>;

  if (!today.has_plan)
    return (
      <Shell>
        <div className="max-w-3xl space-y-6">
          <Guide>No curriculum is running yet. Design one and start it, and the planner will shape each day around your pace.</Guide>
          <Empty
            title="No plan started yet"
            note="Answer three questions on the Curriculum page, start the plan you get back, and your days appear here."
            action={
              <Link to="/curriculum" data-testid="daily-goto-curriculum" className="px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
                Open curriculum
              </Link>
            }
          />
        </div>
      </Shell>
    );

  const stats = today.stats || {};

  return (
    <Shell>
      <div className="grid lg:grid-cols-12 gap-8 reveal" data-testid="daily-dashboard">
        <div className="lg:col-span-8">
          <p className="rule-label">
            {today.curriculum?.label} · about {today.curriculum?.daily_topic_minutes} min a day
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Today's learning</h1>

          <div className={`mt-6 card-flat p-6 ${today.pace_status === "behind" ? "!border-amber-300 bg-amber-50/50" : ""}`} data-testid="daily-pace">
            <div className="flex flex-wrap items-center gap-3">
              <Pill tone={paceTone[today.pace_status] || "slate"} testId="daily-pace-status">
                <TrendingUp className="w-3 h-3" /> {paceLabel[today.pace_status] || today.pace_status}
              </Pill>
              <Pill tone="slate">
                <CalendarClock className="w-3 h-3" /> {today.available_days} days available
              </Pill>
              <Pill tone="slate">
                {stats.completed_topics}/{stats.total_topics} topics done
              </Pill>
              <button data-testid="daily-replan" onClick={replan} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Re-plan
              </button>
            </div>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed" data-testid="daily-pace-message">{today.message}</p>
          </div>

          <div className="mt-6 space-y-4">
            {today.items.map((item) => (
              <div key={item.topic_id} className="card-flat p-6" data-testid={`daily-topic-${item.topic_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="max-w-xl">
                    <div className="flex flex-wrap items-center gap-2">
                      <Pill tone="blue">{item.subject}</Pill>
                      <Pill tone="slate">{item.chapter}</Pill>
                      {item.status === "completed" && <Pill tone="green">completed · {item.best_score}%</Pill>}
                      {!item.material_ready && item.status !== "completed" && <Pill tone="amber">material generating…</Pill>}
                    </div>
                    <h3 className="mt-3 font-display text-xl font-semibold">{item.title}</h3>
                    {item.reason && <p className="mt-1 text-sm text-slate-500">{item.reason}</p>}
                  </div>
                  <button
                    data-testid={`daily-start-${item.topic_id}`}
                    onClick={() => navigate(`/topic/${item.topic_id}`)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors shrink-0"
                  >
                    {item.status === "completed" ? "Revisit" : "Start"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {!today.items.length && <Empty title="Nothing scheduled" note="Open any topic from the library — the planner will adjust around you." />}
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="card-flat p-7">
            <p className="rule-label">Session progress</p>
            <p className="mt-4 font-display text-5xl font-extrabold" data-testid="daily-percent">
              {stats.total_topics ? Math.round((stats.completed_topics * 100) / stats.total_topics) : 0}%
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {stats.completed_topics} of {stats.total_topics} topics · day {stats.days_elapsed}
            </p>
            <div className="mt-5 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${stats.total_topics ? Math.round((stats.completed_topics * 100) / stats.total_topics) : 0}%` }}
              />
            </div>
          </div>

          {today.recommendations?.length > 0 && (
            <div className="card-flat p-7 bg-slate-900 text-white border-slate-800" data-testid="daily-recommendations">
              <p className="rule-label !text-slate-400">Planner suggests</p>
              <ul className="mt-4 space-y-4">
                {today.recommendations.map((r) => (
                  <li key={r.topic_id}>
                    <button onClick={() => navigate(`/topic/${r.topic_id}`)} className="text-left" data-testid={`daily-rec-${r.topic_id}`}>
                      <p className="text-sm font-semibold">{r.title}</p>
                      <p className="text-sm text-slate-400 mt-0.5">{r.note}</p>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-slate-500">Suggestions only — you decide the order.</p>
            </div>
          )}

          <Link to="/library" data-testid="daily-goto-library" className="block card-flat p-6 hover:!border-slate-900">
            <Compass className="w-4 h-4 text-teal-700" />
            <p className="mt-3 font-display font-semibold">Browse the library</p>
            <p className="text-sm text-slate-500 mt-1">Open any topic, any time. The plan reshapes itself.</p>
          </Link>
        </aside>
      </div>
    </Shell>
  );
}
